import React, { useState, useEffect, useCallback } from "react";
import { Row, Col } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useNavigate } from "react-router-dom";
import { useLocation } from "react-router-dom";
import SearchWithFreeTextAPI from "../../common/searchbox-free-text";
import DeleteModal from "../../common/delete-modal";
import DOMPurify from "dompurify";

//SVG
import Search from "../../../assets/svg/searchicon.svg";
import Plus from "../../../assets/svg/pluswhite.svg";
import NoData from "../../../assets/svg/nodata.svg";
import PageLeft from "../../../assets/svg/pageleft.svg";
import PageRight from "../../../assets/svg/pageright.svg";
import Drag from "../../../assets/svg/dots.svg";
import { ReactComponent as EditRow } from "../../../assets/svg/edit.svg";
import { ReactComponent as DeleteRow } from "../../../assets/svg/delete.svg";

//API
import { getGroupApi, getGroupDetailsApi, getMasterApi, saveGroupApi, updateGroupApi } from "./template-api";

const TemplateList = () => {
  const apiUrl = process.env.REACT_APP_API_URL;

  const navigate = useNavigate();

  //Inputs
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoveredGroupIndex, setHoveredGroupIndex] = useState(null);
  const location = useLocation();
  const id = location.state?.id;
  const [validations, setValidations] = useState({
    code: "",
    name: "",
    sms_template: "",
    email_template: "",
    app_template: "",
  });

  //Tab
  const [selectedTab, setSelectedTab] = useState("master");

  // Master Dropdown
  const [selectedMasterSort, setSelectedMasterSort] = useState("");
  const [masterSortField, setMasterSortField] = useState("");
  const [masterSortDir, setMasterSortDir] = useState("");

  const handleMasterSortChange = (e) => {
    const value = e.target.value;
    setSelectedMasterSort(value);

    if (value) {
      const [field, dir] = value.split(" ");
      setMasterSortField(field);
      setMasterSortDir(dir);
    } else {
      setMasterSortField("");
      setMasterSortDir("");
    }
  };

  // Group Dropdown
  const [selectedGroupSort, setSelectedGroupSort] = useState("");
  const [groupSortField, setGroupSortField] = useState("");
  const [groupSortDir, setGroupSortDir] = useState("");

  const handleGroupSortChange = (e) => {
    const value = e.target.value;
    setSelectedGroupSort(value);

    if (value) {
      const [field, dir] = value.split(" ");
      setGroupSortField(field);
      setGroupSortDir(dir);
    } else {
      setGroupSortField("");
      setGroupSortDir("");
    }
  };

  //Master
  const [selectedRows, setSelectedRows] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [masterList, setMasterList] = useState([]);

  const handleSelectRow = (index) => {
    setSelectedRows((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows(masterList.map((_, index) => index));
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    if (selectedRows.length === masterList.length && masterList.length > 0) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedRows, masterList]);

  const [masterType, setMasterType] = useState("");
  const [masterCount, setMasterCount] = useState(0);
  const [masterPageCount, setMasterPageCount] = useState("");
  const [masterSearch, setMasterSearch] = useState("");
  const [itemOffsetMaster, setItemOffsetMaster] = useState("");
  const [itemsPerMasterPage, setItemsPerMasterPage] = useState(10);

  const handleMasterPageClick = useCallback(
    (event) => {
      const newOffset = (event.selected * itemsPerMasterPage) % masterList.length;
      setItemOffsetMaster(event.selected);
    },
    [itemsPerMasterPage, masterList.length]
  );

  const getMasterList = async () => {
    try {
      const response = await getMasterApi(`?medium=${masterType}&search=${masterSearch}&limit=${itemsPerMasterPage}&skip=${itemOffsetMaster * itemsPerMasterPage}&sortByField=${masterSortField}&sortBy=${masterSortDir}`);
      if (response.data.success === true) {
        setMasterList(response.data.templates);
        setMasterCount(response.data.total_rec);
        setMasterPageCount(response.data.total_pgs);
      }
      return response.data;
    } catch (err) {
      console.error("Error fetching master data:", err);
    }
  };

  useEffect(() => {
    getMasterList();
  }, [masterSearch, itemOffsetMaster, itemsPerMasterPage, masterType, masterSortField, masterSortDir]);

  //Group

  const [selectedGroupRows, setSelectedGroupRows] = useState([]);
  const [selectAllGroups, setSelectAllGroups] = useState(false);
  const [groupList, setGroupList] = useState([]);
  const [groupType, setGroupType] = useState("");

  const [groupCount, setGroupCount] = useState("");
  const [groupPageCount, setGroupPageCount] = useState("");
  const [groupSearch, setGroupSearch] = useState("");
  const [itemOffsetGroup, setItemOffsetGroup] = useState(0);
  const [itemsPerGroupPage, setItemsPerGroupPage] = useState(10);

  const handleSelectGroupRow = (index) => {
    setSelectedGroupRows((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  const handleSelectAllGroups = () => {
    if (selectAllGroups) {
      setSelectedGroupRows([]);
    } else {
      setSelectedGroupRows(groupList.map((_, index) => index));
    }
    setSelectAllGroups(!selectAllGroups);
  };

  useEffect(() => {
    if (selectedGroupRows.length === groupList.length && groupList.length > 0) {
      setSelectAllGroups(true);
    } else {
      setSelectAllGroups(false);
    }
  }, [selectedGroupRows, groupList]);

  const handleGroupPageClick = useCallback(
    (event) => {
      const newOffset = (event.selected * itemsPerGroupPage) % groupList.length;
      setItemOffsetGroup(event.selected);
    },
    [itemsPerGroupPage, groupList.length]
  );

  const getGroupList = async () => {
    try {
      const response = await getGroupApi(`?medium=${groupType}&search=${groupSearch}&limit=${itemsPerGroupPage}&skip=${itemOffsetGroup * itemsPerGroupPage}&sortByField=${groupSortField}&sortBy=${groupSortDir}`);
      if (response.data.success === true) {
        setGroupList(response.data.groups);
        setGroupCount(response.data.total_rec);
        setGroupPageCount(response.data.total_pgs);
      }
    } catch (err) {
      console.error("Error fetching group data:", err);
    }
  };

  useEffect(() => {
    getGroupList();
  }, [groupSearch, itemOffsetGroup, itemsPerGroupPage, groupType, groupSortField, groupSortDir]);

  //Group Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);

  const openModal = (id) => {
    setEditingId(id);
    setSelectedId(id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedId(null);
    setEditingId(null);
    resetForm();
  };

  //Group Modal Dropdown
  const [selectedSms, setSelectedSms] = useState(null);

  const handleSmsChange = (selectedOptions) => {
    setSelectedSms(selectedOptions);
  };

  const handleSmsOnChange = (term) => {
    setSelectedSms(term);
  };

  const [selectedApp, setSelectedApp] = useState(null);

  const handleAppChange = (selectedOptions) => {
    setSelectedApp(selectedOptions);
  };

  const handleAppOnChange = (term) => {
    setSelectedApp(term);
  };

  const [selectedEmail, setSelectedEmail] = useState(null);

  const handleEmailChange = (selectedOptions) => {
    setSelectedEmail(selectedOptions);
  };

  const handleEmailOnChange = (term) => {
    setSelectedEmail(term);
  };
  //Reset
  const resetForm = () => {
    setName("");
    setCode("");
    setSelectedSms(null);
    setSelectedApp(null);
    setSelectedEmail(null);
    setValidations({
      code: "",
      name: "",
      sms_template_id: "",
      email_template_id: "",
      app_template_id: "",
    });
  };

  //Group Save

  const [deleteMasterId, setDeleteMasterId] = useState(null);
  const [isOpenDeleteMaster, setIsOpenDeleteMaster] = useState(false);
  useEffect(() => {
    if (deleteMasterId != null) {
      setIsOpenDeleteMaster(true);
    }
  }, [deleteMasterId]);

  const cancelDeleteMaster = () => {
    setIsOpenDeleteMaster(false);
  };
  const onSuccessDeleteMaster = (response) => {
    setDeleteMasterId(null);
    if (response.success === true) {
      setIsOpenDeleteMaster(false);
      getMasterList();
    }
  };

  //Group Delete

  const [deleteGroupId, setDeleteGroupId] = useState(null);
  const [isOpenDeleteGroup, setIsOpenDeleteGroup] = useState(false);
  useEffect(() => {
    if (deleteGroupId != null) {
      setIsOpenDeleteGroup(true);
    }
  }, [deleteGroupId]);

  const cancelDeleteGroup = () => {
    setIsOpenDeleteGroup(false);
  };
  const onSuccessDeleteGroup = (response) => {
    setDeleteGroupId(null);
    if (response.success === true) {
      setIsOpenDeleteGroup(false);
      getGroupList();
    }
  };

  //Group Edit
  const [editingId, setEditingId] = useState(null);

  const getGroupDetails = async (id) => {
    try {
      const response = await getGroupDetailsApi(id);
      if (response.success === true && response.group) {
        const group = response.group;
        setName(group.name ?? "");
        setCode(group.code ?? "");

        setSelectedSms(group.sms_template_id ? { value: group.sms_template_id, label: group.sms_template } : null);
        setSelectedApp(group.app_template_id ? { value: group.app_template_id, label: group.app_template } : null);
        setSelectedEmail(group.email_template_id ? { value: group.email_template_id, label: group.email_template } : null);
      }
    } catch (err) {
      console.error("Error fetching group details:", err);
    }
  };

  useEffect(() => {
    if (editingId != null) {
      getGroupDetails(editingId);
    }
  }, [editingId]);

  //Group Save
  const submitGroup = async () => {
    const dataBeforeSanitize = {
      code: code ?? "",
      name: name ?? "",
      sms_template_id: selectedSms?.value || null,
      email_template_id: selectedEmail?.value || null,
      app_template_id: selectedApp?.value || null,
    };

    const dataAfterSanitize = {};
    Object.entries(dataBeforeSanitize).forEach(([key, value]) => {
      if (typeof value === "boolean" || typeof value === "number" || Array.isArray(value)) {
        dataAfterSanitize[key] = value;
      } else {
        dataAfterSanitize[key] = DOMPurify.sanitize(value);
      }
    });

    let response;
    if (editingId != null) {
      response = await updateGroupApi(dataAfterSanitize, editingId);
    } else {
      response = await saveGroupApi(dataAfterSanitize);
    }

    if (response?.status === 200 && response.data.success === true) {
      setEditingId(null);

      getGroupList();
      closeModal();
    }

    if (response?.status === 400) {
      const validationTemp = {
        code: "",
        name: "",
        sms_template_id: "",
        email_template_id: "",
        app_template_id: "",
      };

      Object.entries(response.data.error || {}).forEach(([key, value]) => {
        const baseKey = key.includes(".") ? key.split(".")[0] : key;
        if (baseKey in validationTemp) {
          validationTemp[baseKey] = Array.isArray(value) && value.length > 0 ? value[0] : "";
        }
      });

      setValidations(validationTemp);
    }
  };

  return (
    <div style={{ fontFamily: "Lexend Deca", width: "100%", height: "100%", backgroundColor: "#F0F4F9", padding: "16px" }}>
      {isOpenDeleteMaster && <DeleteModal deleteUrl={`template/delete/${deleteMasterId}`} onSuccess={onSuccessDeleteMaster} onCancel={cancelDeleteMaster} />}
      {isOpenDeleteGroup && <DeleteModal deleteUrl={`template_group/delete/${deleteGroupId}`} onSuccess={onSuccessDeleteGroup} onCancel={cancelDeleteGroup} />}

      <div style={{ backgroundColor: "#ffffff", padding: "16px", borderRadius: "16px" }}>
        <Row style={{ marginBottom: "24px" }}>
          <Col sm={12}>
            <div style={{ fontWeight: "700", fontSize: "32px", color: "#172b4d", marginBottom: "12px" }}>Masters</div>

            <div style={{ fontWeight: "300", fontSize: "16px", color: "#172b4d" }}>Configure the masters here effortlessly.</div>
          </Col>
        </Row>

        <Row style={{ marginBottom: "24px" }}>
          <Col sm={12}>
            <div style={{ display: "flex", alignItems: "center", width: "100%", borderBottom: "1px solid #d4dde7" }}>
              <div onClick={() => setSelectedTab("master")} style={{ fontWeight: selectedTab === "master" ? "700" : "500", fontSize: "14px", color: selectedTab === "master" ? "#1967d2" : "#172b4d", lineHeight: "100%", height: "40px", padding: "10px 18px", borderBottom: selectedTab === "master" ? "2px solid #1967d2" : "none", cursor: "pointer" }}>
                Template Master
              </div>
              <div onClick={() => setSelectedTab("group")} style={{ fontWeight: selectedTab === "group" ? "700" : "500", fontSize: "14px", color: selectedTab === "group" ? "#1967d2" : "#172b4d", lineHeight: "100%", height: "40px", padding: "10px 18px", borderBottom: selectedTab === "group" ? "2px solid #1967d2" : "none", cursor: "pointer" }}>
                Template Group
              </div>
            </div>
          </Col>
        </Row>

        {selectedTab === "master" && (
          <div>
            <>
              <Row style={{ marginBottom: "24px" }}>
                <Col lg={4} md={6} className="mb-3">
                  <div style={{ fontWeight: "700", fontSize: "20px", color: "#1967d2" }}>Template master ({masterCount || 0})</div>
                </Col>

                <Col lg={3} md={6} className="mb-3">
                  <div style={{ display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px", border: "1px solid #e5e5e5", padding: "9px 16px", backgroundColor: "#ffffff", height: "40px" }}>
                    <img src={Search} alt="search" style={{ width: "16px" }} />
                    <input type="search" placeholder="Search Master" style={{ border: "none", outline: "none", fontSize: "14px", flex: 1, backgroundColor: "transparent" }} value={masterSearch} onChange={(e) => setMasterSearch(e.target.value)} />
                  </div>
                </Col>

                <Col lg={2} md={4} className="mb-3">
                  <button className="btn rounded-3 me-2" style={{ borderColor: "#e5e5e5", background: "#FFFFFF", padding: "9px 16px", borderRadius: "8px", height: "40px", lineHeight: "100%", color: "#3B84E6", fontWeight: "500", fontSize: "14px" }}>
                    <select style={{ outline: "none", border: "none", color: "#1967d2", fontWeight: "500", fontSize: "14px" }} value={selectedMasterSort} onChange={handleMasterSortChange}>
                      <option value="">Default (data created)</option>
                      <option value="name asc">Name (Asc)</option>
                      <option value="name desc">Name (Dsc)</option>
                      <option value="created asc">Date (Oldest)</option>
                      <option value="created desc">Date (Newest)</option>
                    </select>
                  </button>
                </Col>

                <Col lg={1} md={4} className="mb-3">
                  <button className="btn rounded-3 me-2" style={{ borderColor: "#e5e5e5", background: "#FFFFFF", padding: "9px 16px", borderRadius: "8px", height: "40px", lineHeight: "100%", color: "#1967d2", fontWeight: "500", fontSize: "14px" }}>
                    <select value={masterType} onChange={(e) => setMasterType(e.target.value)} style={{ outline: "none", border: "none", color: "#1967d2", fontWeight: "500", fontSize: "14px" }}>
                      <option value="">Type</option>
                      <option value="app-notification">Mobile</option>
                      <option value="email">Email</option>
                      <option value="sms">SMS</option>
                    </select>
                  </button>
                </Col>

                <Col lg={2} md={4} className="mb-3">
                  <button onClick={() => navigate("/masters/template/add")} style={{ display: "flex", gap: "12px", alignItems: "center", height: "40px", borderRadius: "12px", border: "1px solid #ebebeb", padding: "0 16px", backgroundColor: "#1967d2", width: "fit-content", cursor: "pointer", outline: "none" }}>
                    <img src={Plus} alt="Add" style={{ height: "20px" }} />
                    <span style={{ fontWeight: "600", fontSize: "14px", color: "#ffffff" }}>Add Template</span>
                  </button>
                </Col>
              </Row>

              <Row style={{ marginBottom: "16px" }}>
                <Col sm={12}>
                  <div style={{ width: "100%", overflowX: "auto", borderRadius: "16px" }}>
                    <div style={{ minWidth: "900px", width: "100%" }}>
                      {/* Header Row */}
                      <div style={{ display: "flex" }}>
                        {/* Checkbox Header */}
                        <div style={{ width: "40px", minWidth: "40px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e5e5e5", backgroundColor: "#f5f8fa" }}>
                          <input type="checkbox" checked={selectAll} onChange={handleSelectAll} />
                        </div>

                        {/* Column Headers */}
                        {["Name", "Code", "Type"].map((header, index) => (
                          <div key={index} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e5e5e5", padding: "10px 16px", backgroundColor: "#f5f8fa", height: "40px", boxSizing: "border-box", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            <div title={header} style={{ fontWeight: "500", fontSize: "14px", color: "#172b4d", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                              {header}
                            </div>
                            <div>
                              <img src={Drag} style={{ height: "10px" }} />
                            </div>
                          </div>
                        ))}
                      </div>

                      {/* Data Rows */}
                      {masterList.length > 0 ? (
                        masterList.map((row, rowIndex) => (
                          <div key={rowIndex} style={{ position: "relative", display: "flex" }} onMouseEnter={() => setHoveredIndex(rowIndex)} onMouseLeave={() => setHoveredIndex(null)}>
                            {/* Row Checkbox */}
                            <div style={{ width: "40px", minWidth: "40px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e5e5e5", backgroundColor: "#ffffff" }}>
                              <input type="checkbox" checked={selectedRows.includes(rowIndex)} onChange={() => handleSelectRow(rowIndex)} />
                            </div>

                            {/* Data Cells */}
                            {[row.name, row.code, row.type].map((cell, cellIndex) => (
                              <div key={cellIndex} style={{ flex: 1, border: "1px solid #e5e5e5", padding: "10px 16px", backgroundColor: "#ffffff", height: "40px", boxSizing: "border-box", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                <div title={cell} style={{ fontWeight: "400", fontSize: "14px", color: "#172b4d", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                  {cell}
                                </div>
                              </div>
                            ))}

                            {/* Hover Action Buttons */}
                            {hoveredIndex === rowIndex && (
                              <div style={{ position: "absolute", top: "50%", right: "12px", transform: "translateY(-50%)", zIndex: 1 }}>
                                <div style={{ display: "flex", alignItems: "center" }}>
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate("/masters/template/add", { state: { id: row.id } });
                                    }}
                                    style={{ display: "flex", alignItems: "center", border: "0.6px solid #E5E5E5", cursor: "pointer", justifyContent: "center", width: "32px", height: "32px", borderRadius: "100px", background: "#FFFFFF" }}
                                  >
                                    <EditRow />
                                  </div>
                                  <span style={{ marginLeft: "16px" }}></span>
                                  <div
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setDeleteMasterId(row.id);
                                    }}
                                    style={{ display: "flex", alignItems: "center", border: "0.6px solid #E5E5E5", cursor: "pointer", justifyContent: "center", width: "32px", height: "32px", borderRadius: "100px", background: "#FFFFFF" }}
                                  >
                                    <DeleteRow />
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: "40px 0", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                          <img src={NoData} alt="No Data" style={{ width: "200px", opacity: 0.7, marginBottom: "12px" }} />
                          <div style={{ fontSize: "14px", color: "#999" }}>No Data found</div>
                        </div>
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              <Row style={{ marginTop: "32px" }}>
                <Col sm={12} md={6}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>Records per page :</div>
                    <div>
                      <select value={itemsPerMasterPage} onChange={(e) => setItemsPerMasterPage(e.target.value)} style={{ borderRadius: "6px", height: "32px", border: "1px solid #e5e5e5", padding: "8px 12px", backgroundColor: "#ffffff", fontWeight: "700", fontSize: "13px", color: "#172b4d" }}>
                        <option value="5">5</option>
                        <option value="10">10</option>
                        <option value="20">20</option>
                        <option value="50">50</option>
                      </select>
                    </div>
                  </div>
                </Col>

                <Col sm={12} md={6}>
                  <div className="d-flex justify-content-end">
                    <ReactPaginate previousLabel={<img src={PageLeft} style={{ height: "32px" }} />} nextLabel={<img src={PageRight} style={{ height: "32px" }} />} breakLabel={"..."} pageCount={masterPageCount + 1} marginPagesDisplayed={2} pageRangeDisplayed={3} onPageChange={handleMasterPageClick} containerClassName={"pagination"} activeClassName={"active"} />
                  </div>
                </Col>
              </Row>
            </>
          </div>
        )}

        {selectedTab === "group" && (
          <div>
            <Row style={{ marginBottom: "24px" }}>
              <Col lg={5} md={6} className="mb-3">
                <div style={{ fontWeight: "700", fontSize: "20px", color: "#1967d2" }}>Template Group ({groupCount})</div>
              </Col>

              <Col lg={3} md={6} className="mb-3">
                <div style={{ display: "flex", alignItems: "center", gap: "8px", borderRadius: "8px", border: "1px solid #e5e5e5", padding: "9px 16px", backgroundColor: "#ffffff", height: "40px" }}>
                  <img src={Search} alt="search" style={{ width: "16px" }} />
                  <input type="search" placeholder="Search Group" value={groupSearch} onChange={(e) => setGroupSearch(e.target.value)} style={{ border: "none", outline: "none", fontSize: "14px", flex: 1, backgroundColor: "transparent" }} />
                </div>
              </Col>

              <Col lg={2} md={4} className="mb-3">
                <button className="btn rounded-3 me-2" style={{ borderColor: "#e5e5e5", background: "#FFFFFF", padding: "9px 16px", borderRadius: "8px", height: "40px", lineHeight: "100%", color: "#1967d2", fontWeight: "500", fontSize: "14px" }}>
                  <select style={{ outline: "none", border: "none", color: "#1967d2", fontWeight: "500", fontSize: "14px" }} value={selectedGroupSort} onChange={handleGroupSortChange}>
                    <option value="">Default (data created)</option>
                    <option value="name asc">Name (Asc)</option>
                    <option value="name desc">Name (Dsc)</option>
                    <option value="created asc">Date (Oldest)</option>
                    <option value="created desc">Date (Newest)</option>
                  </select>
                </button>
              </Col>

              <Col lg={2} md={4} className="mb-3">
                <>
                  {/* Trigger Button */}
                  <button onClick={() => openModal(null)} style={{ display: "flex", gap: "12px", alignItems: "center", height: "40px", borderRadius: "12px", border: "1px solid #ebebeb", padding: "0 16px", backgroundColor: "#1967d2", width: "fit-content", cursor: "pointer", outline: "none" }}>
                    <img src={Plus} alt="Add" style={{ height: "20px" }} />
                    <span style={{ fontWeight: "600", fontSize: "14px", color: "#ffffff" }}>Add Group</span>
                  </button>

                  {/* Modal */}
                  {isModalOpen && (
                    <div style={{ position: "fixed", top: 0, left: 0, width: "100vw", height: "100vh", backgroundColor: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
                      <div style={{ backgroundColor: "#ffffff", borderRadius: "24px", padding: "24px", width: "500px", boxShadow: "0 0 20px rgba(0,0,0,0.2)", position: "relative" }}>
                        <div style={{ fontSize: "20px", color: "#172b4d", fontWeight: "600", marginBottom: "24px" }}>Add template group</div>
                        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                            <div style={{ fontSize: "20px", color: "#172b4d", fontWeight: "600", marginBottom: "12px" }}>
                              <div style={{ fontSize: "13px", color: "#172b4d", fontWeight: "400", marginBottom: "8px" }}>Name</div>
                              <input value={name} onChange={(e) => setName(e.target.value)} type="text" placeholder="Enter name" style={{ fontWeight: "400", fontSize: "14px", color: "#172b4d", lineHeight: "100%", height: "40px", borderRadius: "10px", borderRadius: "10px", border: "1.5px solid #ebebeb", padding: "16px", width: "100%" }} /> <span style={{ fontWeight: "300", fontSize: "12px", color: "#ff4444" }}>{validations.name}</span>
                            </div>

                            <div style={{ fontSize: "20px", color: "#172b4d", fontWeight: "600", marginBottom: "12px" }}>
                              <div style={{ fontSize: "13px", color: "#172b4d", fontWeight: "400", marginBottom: "8px" }}>Code</div>
                              <input value={code} onChange={(e) => setCode(e.target.value)} type="text" placeholder="Enter code" style={{ fontWeight: "400", fontSize: "14px", color: "#172b4d", lineHeight: "100%", height: "40px", borderRadius: "10px", borderRadius: "10px", border: "1.5px solid #ebebeb", padding: "16px", width: "100%" }} /> <span style={{ fontWeight: "300", fontSize: "12px", color: "#ff4444" }}>{validations.code}</span>
                            </div>

                            <div style={{ fontSize: "20px", color: "#172b4d", fontWeight: "600", marginBottom: "12px" }}>
                              <div style={{ fontSize: "13px", color: "#172b4d", fontWeight: "400", marginBottom: "13px" }}>SMS Template</div>
                              <SearchWithFreeTextAPI onDropdownChange={handleSmsChange} onChangeSearch={handleSmsOnChange} apiUrl={apiUrl + `get_template?term&medium=sms`} params={{}} searchLimit={0} placeholder={"Search SMS"} selectedValue={selectedSms} />
                              <span style={{ fontWeight: "300", fontSize: "12px", color: "#ff4444" }}>{validations.sms_template}</span>
                            </div>

                            <div style={{ fontSize: "20px", color: "#172b4d", fontWeight: "600", marginBottom: "12px" }}>
                              <div style={{ fontSize: "13px", color: "#172b4d", fontWeight: "400", marginBottom: "13px" }}>App Notification</div>
                              <SearchWithFreeTextAPI onDropdownChange={handleAppChange} onChangeSearch={handleAppOnChange} apiUrl={apiUrl + `get_template?term&medium=app-notification`} params={{}} searchLimit={0} placeholder={"Search App"} selectedValue={selectedApp} />
                              <span style={{ fontWeight: "300", fontSize: "12px", color: "#ff4444" }}>{validations.app_template}</span>
                            </div>

                            <div style={{ fontSize: "20px", color: "#172b4d", fontWeight: "600", marginBottom: "12px" }}>
                              <div style={{ fontSize: "13px", color: "#172b4d", fontWeight: "400", marginBottom: "13px" }}>Email Template</div>
                              <SearchWithFreeTextAPI onDropdownChange={handleEmailChange} onChangeSearch={handleEmailOnChange} apiUrl={apiUrl + `get_template?term&medium=email`} params={{}} searchLimit={0} placeholder={"Search Email"} selectedValue={selectedEmail} />
                              <span style={{ fontWeight: "300", fontSize: "12px", color: "#ff4444" }}>{validations.email_template}</span>
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "flex-end" }}>
                          <button
                            onClick={() => {
                              resetForm();
                              closeModal();
                            }}
                            style={{ height: "40px", lineHeight: "100%", borderRadius: "100px", border: "1px solid #d4dde7", padding: "10px 28px", fontWeight: "600", fontSize: "15px", color: "#172b4d", backgroundColor: "#ffffff" }}
                          >
                            Cancel
                          </button>

                          <button onClick={submitGroup} style={{ height: "40px", lineHeight: "100%", borderRadius: "100px", border: "1px solid #d4dde7", padding: "10px 28px", fontWeight: "600", fontSize: "15px", color: "#ffffff", backgroundColor: "#1967d2" }}>
                            Save
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              </Col>
            </Row>

            <Row style={{ marginBottom: "16px" }}>
              <Col sm={12}>
                <div style={{ width: "100%", overflowX: "auto", borderRadius: "16px" }}>
                  <div style={{ minWidth: "900px", width: "100%" }}>
                    {/* Header Row */}
                    <div style={{ display: "flex" }}>
                      <div style={{ width: "40px", minWidth: "40px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e5e5e5", backgroundColor: "#f5f8fa" }}>
                        <input type="checkbox" checked={selectAllGroups} onChange={handleSelectAllGroups} />
                      </div>
                      {["Name", "Code", "SMS Template", "Email Template", "Notification Template"].map((header, index) => (
                        <div key={index} style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", border: "1px solid #e5e5e5", padding: "10px 16px", backgroundColor: "#f5f8fa", height: "40px", boxSizing: "border-box", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          <div title={header} style={{ fontWeight: "500", fontSize: "14px", color: "#172b4d", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                            {header}
                          </div>
                          <div>
                            <img src={Drag} style={{ height: "10px" }} />
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* Data Rows */}
                    {groupList.length > 0 ? (
                      groupList.map((row, rowIndex) => (
                        <div key={rowIndex} style={{ position: "relative", display: "flex" }} onMouseEnter={() => setHoveredGroupIndex(rowIndex)} onMouseLeave={() => setHoveredGroupIndex(null)}>
                          <div style={{ width: "40px", minWidth: "40px", display: "flex", alignItems: "center", justifyContent: "center", border: "1px solid #e5e5e5", backgroundColor: "#ffffff" }}>
                            <input type="checkbox" checked={selectedGroupRows.includes(rowIndex)} onChange={() => handleSelectGroupRow(rowIndex)} />
                          </div>
                          {[row.name, row.code, row.sms_template, row.email_template, row.app_template].map((cell, cellIndex) => (
                            <div key={cellIndex} style={{ flex: 1, border: "1px solid #e5e5e5", padding: "10px 16px", backgroundColor: "#ffffff", height: "40px", boxSizing: "border-box", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                              <div title={cell} style={{ fontWeight: "400", fontSize: "14px", color: "#172b4d", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                {cell}
                              </div>
                            </div>
                          ))}
                          {hoveredGroupIndex === rowIndex && (
                            <div style={{ position: "absolute", top: "50%", right: "12px", transform: "translateY(-50%)", zIndex: 1 }}>
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openModal(row.id);
                                  }}
                                  style={{ display: "flex", alignItems: "center", border: "0.6px solid #E5E5E5", cursor: "pointer", justifyContent: "center", width: "32px", height: "32px", borderRadius: "100px", background: "#FFFFFF" }}
                                >
                                  <EditRow />
                                </div>
                                <span style={{ marginLeft: "16px" }}></span>
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDeleteGroupId(row.id);
                                  }}
                                  style={{ display: "flex", alignItems: "center", border: "0.6px solid #E5E5E5", cursor: "pointer", justifyContent: "center", width: "32px", height: "32px", borderRadius: "100px", background: "#FFFFFF" }}
                                >
                                  <DeleteRow />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      ))
                    ) : (
                      <div style={{ padding: "40px 0", display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column" }}>
                        <img src={NoData} alt="No Data" style={{ width: "200px", opacity: 0.7, marginBottom: "12px" }} />
                        <div style={{ fontSize: "14px", color: "#999" }}>No group data found</div>
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            <Row style={{ marginTop: "32px" }}>
              <Col sm={12} md={6}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <div style={{ fontWeight: "400", fontSize: "13px", color: "#172b4d" }}>Records per page :</div>
                  <select value={itemsPerGroupPage} onChange={(e) => setItemsPerGroupPage(e.target.value)} style={{ borderRadius: "6px", height: "32px", border: "1px solid #e5e5e5", padding: "8px 12px", backgroundColor: "#ffffff", fontWeight: "700", fontSize: "13px", color: "#172b4d" }}>
                    <option value="5">5</option>
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                  </select>
                </div>
              </Col>

              <Col sm={12} md={6}>
                <div className="d-flex justify-content-end">
                  <ReactPaginate previousLabel={<img src={PageLeft} style={{ height: "32px" }} />} nextLabel={<img src={PageRight} style={{ height: "32px" }} />} breakLabel={"..."} pageCount={groupPageCount + 1} marginPagesDisplayed={2} pageRangeDisplayed={3} onPageChange={handleGroupPageClick} containerClassName={"pagination"} activeClassName={"active"} />
                </div>
              </Col>
            </Row>
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateList;
